import {ChangeDetectionStrategy, Component, OnInit, signal} from '@angular/core';
import {PageTitleComponent} from "../../../components/page-title/page-title.component";
import {CustomFormComponent} from "../../../components/custom-form/custom-form.component";
import {TextInputComponent} from "../../../components/text-input/text-input.component";
import {FormInputsComponent} from "../../../components/form-inputs/form-inputs.component";
import {FormBuilder, Validators} from "@angular/forms";
import {passwordValidator} from "../../../validators";
import {AuthService} from "../../../services/auth/auth.service";
import {catchError, from, of, switchMap, tap} from "rxjs";
import {ActivatedRoute, Router} from "@angular/router";
import {MessageService} from "primeng/api";
import {Message} from "primeng/message";
import {SubmitButtonComponent} from "../../../components/submit-button/submit-button.component";

@Component({
  selector: 'homey-reset-password-page',
  imports: [
    PageTitleComponent,
    CustomFormComponent,
    TextInputComponent,
    FormInputsComponent,
    Message,
    SubmitButtonComponent
  ],
  templateUrl: './reset-password-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResetPasswordPageComponent implements OnInit {
  constructor(
    private readonly fb: FormBuilder,
    private readonly authService: AuthService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly messageService: MessageService,
    private readonly router: Router,
  ) {

  }

  public ngOnInit(): void {
    this.email = this.activatedRoute.snapshot.queryParamMap.get('email');
    const timestampParam = this.activatedRoute.snapshot.queryParamMap.get('timestamp');

    if (timestampParam) {
      const parsedTimestamp = Number(timestampParam);

      if (!Number.isNaN(parsedTimestamp)) {
        const ageMs = Date.now() - parsedTimestamp;

        if (ageMs > 5 * 60 * 1000) {
          this.linkExpired.set(true);
        }
      }
    }
  }

  public readonly form = this.fb.group({
    password: this.fb.nonNullable.control('', [Validators.required, passwordValidator]),
  });

  protected email: string | null = null;
  protected readonly linkExpired = signal(false);
  protected readonly resendSuccess = signal(false);
  protected readonly resendError = signal<string | undefined>(undefined);

  protected readonly submit = (form: { password: string }) => {
    const code = this.activatedRoute.snapshot.queryParamMap.get('code');
    const email = this.email;

    if (!code || !email) {
      throw new Error('Missing code or email');
    }

    return from(this.authService.homeyConfirmResetPassword(
      email,
      code,
    form.password,
    )).pipe(
      tap(() => this.messageService.add({
        severity: 'success',
        summary: 'Password reset successful!',
        detail: 'Your password has been reset successfully'
      })),
      switchMap(() => this.router.navigate(['..'], {relativeTo: this.activatedRoute}))
    );
  }

  protected readonly resendResetLink = () => {
    if (!this.email) {
      this.resendError.set('We could not find your email from this link. Please start the reset process again.');
      return of(undefined);
    }

    this.resendError.set(undefined);
    this.resendSuccess.set(false);

    return from(this.authService.homeyForgotPassword(this.email)).pipe(
      tap(() => this.resendSuccess.set(true)),
      catchError((error) => {
        console.error('Error resending reset password email', error);
        this.resendError.set('We could not send a new reset email. Please try again.');
        return of(undefined);
      })
    );
  }
}
